import { createSelector } from '@ngrx/store';
import { docsFeature } from '../docs/docs.reducer';
import { docKey } from '../docs/docs.normalize';
import { selectActiveProjectId } from '../workspace/workspace.selectors';
import {
  ChatMessage,
  DbDiagramLayout,
  DbSchema,
  RepoContext,
  extractLayout,
  extractSchema,
  normalizeRepoContext,
} from './db-design.models';

const { selectEntities, selectKeyIndex, selectStatusByKey, selectErrorByKey } = docsFeature;

function dataFor(
  entities: ReturnType<typeof selectEntities>,
  keyIndex: Record<string, string>,
  pid: string | null,
  key: string
): any {
  if (!pid) return null;
  const id = keyIndex[docKey(pid, key)];
  return id ? entities.entities[id]?.data ?? null : null;
}

export const selectDbSchema = createSelector(
  selectActiveProjectId,
  selectEntities,
  selectKeyIndex,
  (pid, e, k): DbSchema | null => extractSchema(dataFor(e, k, pid, 'db-design.main'))
);

export const selectDbLayout = createSelector(
  selectActiveProjectId,
  selectEntities,
  selectKeyIndex,
  (pid, e, k): DbDiagramLayout | null => extractLayout(dataFor(e, k, pid, 'db-design.layout'))
);

export const selectDbChatMessages = createSelector(
  selectActiveProjectId,
  selectEntities,
  selectKeyIndex,
  (pid, e, k): ChatMessage[] => {
    const d = dataFor(e, k, pid, 'db-design.chat');
    return Array.isArray(d?.messages) ? (d.messages as ChatMessage[]) : [];
  }
);

export const selectDbProjectDefinition = createSelector(
  selectActiveProjectId,
  selectEntities,
  selectKeyIndex,
  (pid, e, k): any => dataFor(e, k, pid, 'define.definition')
);

export const selectDbRepoContext = createSelector(
  selectActiveProjectId,
  selectEntities,
  selectKeyIndex,
  (pid, e, k): RepoContext | null => normalizeRepoContext(dataFor(e, k, pid, 'repos.settings'))
);

export const selectDbLoading = createSelector(selectActiveProjectId, selectStatusByKey, (pid, s) =>
  pid ? s[docKey(pid, 'db-design.main')] === 'loading' : false
);

export const selectDbError = createSelector(selectActiveProjectId, selectErrorByKey, (pid, e) =>
  pid ? e[docKey(pid, 'db-design.main')] ?? null : null
);
